"use client";

import Image from "next/image";
// CMS-supplied hrefs are emitted verbatim - do NOT swap this for @repo/ui/navigation.
// The CMS localizes its own URLs (e.g. "cl/es/centro-de-ayuda/") and also returns
// external/relative ones, so prefixing here would double or corrupt the locale.
import Link from "next/link";

import { ChevronRight } from "lucide-react";

import { Card, CardContent, CardFooter, CardHeader } from "@repo/ui/components/ui/card";
import type { ImageCard } from "@repo/ui/types/cms";

export interface ImageCardSmallProps {
  item: ImageCard;
}

export default function ImageCardSmall({ item }: Readonly<ImageCardSmallProps>) {
  return (
    <Card
      className="gap-0 overflow-hidden rounded-lg border border-brand-black-70 bg-white py-0 shadow-none !p-0"
      data-testid="image-card-small-section"
    >
      {item?.image && (
        <CardHeader className="!p-0" data-testid="image-card-header-section">
          <Image
            src={item.image}
            alt={item.heading || "promo"}
            width={326}
            height={180}
            className="w-full object-cover"
            data-testid="image-card-image"
          />
        </CardHeader>
      )}

      <CardContent className="px-4 py-2 text-left" data-testid="image-card-content-section">
        {item?.heading && (
          <p className="text-brand-blue text-base font-bold" data-testid="image-card-heading-lbl">
            {item.heading}
          </p>
        )}

        {item?.description && (
          <div
            className="text-brand-black-30 mt-1 text-sm [&_p]:m-0"
            dangerouslySetInnerHTML={{ __html: item.description }}
            data-testid="image-card-description-section"
          />
        )}
      </CardContent>

      {item?.link_cta && item?.link_text && (
        <CardFooter
          className="mx-4 flex items-center justify-center border-t border-brand-black-70 !p-2"
          data-testid="image-card-footer-section"
        >
          <Link href={item.link_cta} target="_blank" data-testid="image-card-link">
            <button
              className="text-brand-cyan cursor-pointer text-sm font-medium"
              data-testid="image-card-btn"
            >
              {item.link_text}
              <ChevronRight className="ml-1 inline-block" size={14} />
            </button>
          </Link>
        </CardFooter>
      )}
    </Card>
  );
}
